import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useTranslation } from '../../contexts/TranslationContext';

const DropdownNavigation = ({ className }) => {
  const { t, language } = useTranslation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [previousDropdown, setPreviousDropdown] = useState(null);
  const timeoutRef = useRef(null);

  const navigationItems = [
    {
      title: t('nav.company'),
      items: [
        {
          name: t('pages.company.about.title'),
          href: "/about",
          subtitle: t('pages.company.about.subtitle')
        },
        {
          name: t('pages.company.technology.title'),
          href: "/technology",
          subtitle: t('pages.company.technology.subtitle')
        },
        {
          name: t('pages.company.vision2030.title'),
          href: "/vision-2030",
          subtitle: t('pages.company.vision2030.subtitle')
        },
        {
          name: t('pages.careers.title'),
          href: "/careers", 
          subtitle: t('pages.careers.subtitle')
        }
      ]
    },
    {
      title: t('nav.services'),
      items: [
        {
          name: t('pages.services.title'),
          href: "/services",
          subtitle: t('pages.services.subtitle')
        },
        {
          name: t('pages.solutions.title'),
          href: "/solutions",
          subtitle: t('pages.solutions.subtitle')
        },
        {
          name: t('pages.caseStudies.title'),
          href: "/case-studies",
          subtitle: t('pages.caseStudies.subtitle')
        },
        {
          name: t('pages.blog.title'),
          href: "/blog",
          subtitle: t('pages.blog.subtitle')
        },
        {
          name: t('pages.testimonials.title'),
          href: "/testimonials",
          subtitle: t('pages.testimonials.subtitle')
        }
      ]
    },
    {
      title: t('nav.getStarted'),
      items: [
        {
          name: t('pages.contact.title'),
          href: "/contact",
          subtitle: t('pages.contact.subtitle')
        },
        {
          name: t('pages.bookConsultation.title'),
          href: "/book-consultation",
          subtitle: t('pages.bookConsultation.subtitle')
        },
        {
          name: t('pages.privacyPolicy.title'),
          href: "/privacy-policy",
          subtitle: t('pages.privacyPolicy.subtitle')
        },
        {
          name: t('pages.termsOfService.title'),
          href: "/terms-of-service",
          subtitle: t('pages.termsOfService.subtitle')
        }
      ]
    }
  ];

  const handleMouseEnter = (index) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Store previous dropdown for smooth transition
    if (activeDropdown !== null && activeDropdown !== index) {
      setPreviousDropdown(activeDropdown);
    }
    setActiveDropdown(index);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing to allow moving to dropdown
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
      setPreviousDropdown(null);
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    // Clear timeout when mouse enters dropdown
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    // Close dropdown when mouse leaves the dropdown area
    setActiveDropdown(null);
    setPreviousDropdown(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Clear previousDropdown after transition completes
  useEffect(() => {
    if (previousDropdown !== null && activeDropdown !== previousDropdown) {
      const timer = setTimeout(() => {
        setPreviousDropdown(null);
      }, 300); // Match the transition duration
      
      return () => clearTimeout(timer);
    }
  }, [activeDropdown, previousDropdown]);

  return (
    <>
      {/* CSS Animation Keyframes */}
      <style jsx>{`
        @keyframes dropdownSlideIn {
          0% {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes dropdownFadeIn {
          0% {
            opacity: 0;
            transform: translateY(-5px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes dropdownFadeOut {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-5px);
          }
        }
      `}</style>
      
      <nav className={cn("relative", className)}>
        <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
        {navigationItems.map((section, sectionIndex) => (
          <div
            key={section.title}
            className="relative"
            onMouseEnter={() => handleMouseEnter(sectionIndex)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Main Navigation Link */}
            <button className="text-muted-foreground hover:text-foreground transition-all duration-200 font-medium px-3 py-2 rounded-md hover:bg-accent/20 relative">
              {section.title}
              {/* Active indicator */}
              {activeDropdown === sectionIndex && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
              )}
            </button>

            {/* Dropdown Menu */}
            {((activeDropdown === sectionIndex) || (previousDropdown === sectionIndex && activeDropdown !== null)) && (
              <div 
                className={`absolute top-full mt-3 bg-background border border-border rounded-xl shadow-2xl z-50 transform transition-all duration-300 ease-out ${
                  activeDropdown === sectionIndex
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 translate-y-2'
                } ${language === 'ar' ? 'right-0' : 'left-0'}`}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
                style={{
                  animation: activeDropdown === sectionIndex 
                    ? 'dropdownFadeIn 0.3s ease-out forwards' 
                    : 'dropdownFadeOut 0.3s ease-out forwards'
                }}
              >
                {/* Arrow */}
                <div className={`absolute -top-2 w-4 h-4 bg-background border-l border-t border-border rotate-45 ${language === 'ar' ? 'right-6' : 'left-6'}`}></div>
                <div className="p-6">
                  {/* Section Header */}
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      {section.title}
                    </h3>
                  </div>
                  
                  {/* Menu Items Grid */}
                  <div className={`grid gap-1 ${section.items.length > 2 ? 'grid-cols-2 w-[576px]' : 'grid-cols-1 w-72'}`}>
                    {section.items.map((item, itemIndex) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block group"
                      >
                        <div className="p-4 rounded-lg hover:bg-accent/30 transition-all duration-200 group-hover:bg-accent/40 min-w-0">
                          <div className="font-semibold text-foreground group-hover:text-primary transition-colors duration-200 text-base">
                            {item.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1 group-hover:text-muted-foreground/80 transition-colors duration-200">
                            {item.subtitle}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
    </>
  );
};

export default DropdownNavigation;
